<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { refDebounced } from '@vueuse/core';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/solid';
import http from '@/services/http';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import type { ApiResponse } from '@/types/types';
import type { IScope } from '@/types/filterBuilderTypes';

/**
 * Pick one record of a model by searching it, for a model too big to load whole.
 *
 * It is a dropdown, like the app's other pickers: the button shows what is chosen, and opening
 * it shows a search box and the first matches. Picking one closes it. It searches with
 * `POST /api/{slug}/search`, matching the name as the user types. When there are more matches
 * than it shows, it says so, so the user knows to type rather than scroll.
 *
 * A `scope` is a filter group that is always sent with the name rule, so the picker only ever
 * offers records inside it. The Create Schedule dialog scopes zones to the list's own zone and
 * the zones that subscribe to it; bulk edit searches every bucket.
 *
 * The `option` slot draws each match, and the `selected` slot draws the button's value, so a zone
 * can be drawn with its mark as zones are everywhere else. Without them, both show the record's
 * display string.
 */
const props = withDefaults(
  defineProps<{
    slug: string;
    testIdPrefix?: string;
    /** A filter group every search is limited to. */
    scope?: IScope | null;
    /** The button's text while nothing is chosen. */
    placeholder?: string;
    searchPlaceholder?: string;
    /** The name of a record chosen before the picker was drawn, such as one a dialog remembered. */
    initialLabel?: string | null;
  }>(),
  { testIdPrefix: undefined, scope: null, placeholder: 'Choose…', searchPlaceholder: 'Type to search by name…', initialLabel: null },
);

/** The chosen id, as a string. */
const modelValue = defineModel<string | null>({ default: null });

const emit = defineEmits<{
  (e: 'label', label: string | null): void;
  /** The chosen record's search document, for a caller that draws more than its name. */
  (e: 'pick', row: Record<string, unknown>): void;
}>();

defineSlots<{
  option?(props: { row: Record<string, unknown>; label: string; selected: boolean }): unknown;
  selected?(props: { label: string | null }): unknown;
}>();

/** How many matches to show. The user narrows by typing rather than by scrolling. */
const RESULT_LIMIT = 20;

const open = ref(false);
const searchText = ref('');
const debouncedSearchText = refDebounced(searchText, 250);
const chosenLabel = ref<string | null>(modelValue.value === null ? null : props.initialLabel);

const testId = computed(() => props.testIdPrefix ?? `model-search-${props.slug}`);

interface SearchOption {
  id: number;
  label: string;
  row: Record<string, unknown>;
}

interface SearchResults {
  options: SearchOption[];
  total: number | null;
}

function searchFilter(text: string): { ruleScopes: IScope } | undefined {
  const rules: IScope[] = [];
  if (text !== '') rules.push({ id: 'model-search-name', column: 'name', criterion: 'contains', value: text, field: null });
  if (props.scope) rules.push(props.scope);
  return rules.length === 0 ? undefined : { ruleScopes: { id: 'root', scopeType: 'and', rules } };
}

const resultsQuery = useQuery({
  queryKey: computed(() => ['model-search', props.slug, JSON.stringify(props.scope), debouncedSearchText.value.trim()] as const),
  queryFn: async (): Promise<SearchResults> => {
    const response = await http.post(`/api/${props.slug}/search`, {
      perPage: RESULT_LIMIT,
      withTotal: true,
      filter: searchFilter(debouncedSearchText.value.trim()),
    });
    const body = response.data as ApiResponse & { meta?: { total?: unknown } };
    const rows = (body.data ?? []) as Record<string, unknown>[];
    return {
      options: rows
        .filter((row) => Number.isInteger(Number(row.id)))
        .map((row) => ({ id: Number(row.id), label: String(row.display_string ?? row.name ?? `#${row.id}`), row })),
      total: typeof body.meta?.total === 'number' ? body.meta.total : null,
    };
  },
  // Only once the list is open: a closed picker costs nothing.
  enabled: open,
  staleTime: 60 * 1000,
  retry: 0,
});

const options = computed(() => resultsQuery.data.value?.options ?? []);
/** How many matches are not shown, so the list can say to type. */
const hiddenCount = computed(() => {
  const total = resultsQuery.data.value?.total;
  return total === null || total === undefined ? 0 : Math.max(0, total - options.value.length);
});

const listElement = ref<HTMLElement | null>(null);

/**
 * Typing stays in the search box: the menu would otherwise treat each letter as typeahead and
 * jump to an item. Escape still closes the menu, and the down arrow moves to the first match.
 */
function onSearchKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' || event.key === 'Tab') return;
  event.stopPropagation();
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    listElement.value?.querySelector<HTMLElement>('[role="option"]')?.focus();
  }
}

function choose(option: SearchOption) {
  modelValue.value = String(option.id);
  chosenLabel.value = option.label;
  emit('label', option.label);
  emit('pick', option.row);
  open.value = false;
}
</script>

<template>
  <DropdownMenu v-model:open="open">
    <DropdownMenuTrigger as-child>
      <Button
        variant="outline"
        type="button"
        class="w-full justify-between pl-3 pr-3 font-normal [&>div]:w-full [&>div>div]:w-full [&>div>div]:justify-between"
        :data-testid="`${testId}-trigger`"
      >
        <span class="min-w-0 truncate text-left" :data-testid="`${testId}-chosen`">
          <slot name="selected" :label="chosenLabel">{{ chosenLabel ?? placeholder }}</slot>
        </span>
        <ChevronDownIcon class="size-4 shrink-0 opacity-50" aria-hidden="true" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent class="w-[var(--reka-dropdown-menu-trigger-width)] min-w-64" align="start" :data-testid="testId">
      <div class="p-2">
        <div class="relative">
          <MagnifyingGlassIcon class="text-muted-foreground absolute top-2.5 left-2 size-4" aria-hidden="true" />
          <Input
            v-model="searchText"
            :placeholder="searchPlaceholder"
            class="h-8 pl-8 text-sm"
            :data-testid="`${testId}-input`"
            @click.stop
            @keydown="onSearchKeydown"
          />
        </div>
      </div>
      <div ref="listElement" class="max-h-60 overflow-y-auto" role="listbox">
        <p v-if="resultsQuery.isLoading.value" class="text-muted-foreground px-2 py-1.5 text-xs">Searching…</p>
        <p v-else-if="resultsQuery.isError.value" class="text-destructive px-2 py-1.5 text-xs">Couldn't search. Try again.</p>
        <p v-else-if="options.length === 0" class="text-muted-foreground px-2 py-1.5 text-xs">Nothing matches.</p>
        <DropdownMenuItem
          v-for="option in options"
          :key="option.id"
          role="option"
          :aria-selected="modelValue === String(option.id)"
          :class="modelValue === String(option.id) ? 'bg-muted font-medium' : ''"
          :data-testid="`${testId}-option-${option.id}`"
          @select="choose(option)"
        >
          <slot name="option" :row="option.row" :label="option.label" :selected="modelValue === String(option.id)">
            <span class="truncate">{{ option.label }}</span>
          </slot>
        </DropdownMenuItem>
        <p v-if="hiddenCount > 0" class="text-muted-foreground border-t px-2 py-1.5 text-xs" :data-testid="`${testId}-more`">
          {{ hiddenCount.toLocaleString() }} more {{ hiddenCount === 1 ? 'match' : 'matches' }}. Type to narrow the list.
        </p>
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
